/* istanbul ignore file */

import { DOCUMENT, inject, Injectable } from '@angular/core';
import { AppStateService } from '@libs/shared/common';
import { debounceTime } from 'rxjs';
import { DebuggerOptionName, DebuggerOptions } from '../debugger.model';

type KebabCase<T extends string> =
  T extends `${infer F}${infer R}`
    ? `${F extends Uppercase<F> ? '-' : ''}${Lowercase<F>}${KebabCase<R>}`
    : '';

type SearchParamMap<T extends DebuggerOptionName = DebuggerOptionName> = {
  [key in T]: KebabCase<key>;
};

@Injectable({ providedIn: 'root' })
export class UrlParamsService {
  private readonly view?: Window = inject(DOCUMENT).defaultView ?? undefined;

  private options: DebuggerOptions = {
    bffEnv: 'stage',
    pageId: '2800',
    customerNumber: '0040299262',
    showNav: 'true',
  };

  private readonly paramsMap: SearchParamMap = {
    bffEnv: 'bff-env',
    pageId: 'page-id',
    customerNumber: 'customer-number',
    showNav: 'show-nav',
  };

  public constructor(
    private readonly appState: AppStateService,
  ) {
    this.appState.get('currentPath')
      .pipe(debounceTime(10))
      .subscribe(() => void this.updateOptions(this.options));
  }

  public readOptions(): DebuggerOptions {
    if (!this.view) {
      return this.options;
    }

    this.options = { ...this.options };

    const searchParams: URLSearchParams = new URLSearchParams(this.view.location.search);

    let shouldUpdateUrl: boolean = false;

    Object.entries(this.paramsMap).forEach(([optionName, paramName]) => {
      const providedOption: string | null = searchParams.get(paramName);

      if (providedOption !== null) {
        this.options[optionName as DebuggerOptionName] = providedOption;
      } else {
        shouldUpdateUrl = true;
      }
    });

    if (shouldUpdateUrl) {
      this.updateOptions(this.options);
    }

    return this.options;
  }

  public updateOptions(options: DebuggerOptions): void {
    if (!this.view) {
      return;
    }

    this.options = { ...options };

    const url: URL = new URL(this.view.location.origin);

    url.pathname = this.view.location.pathname;
    url.hash = this.view.location.hash;

    Object.entries(options).forEach(([key, value]) => {
      url.searchParams.set(this.paramsMap[key as DebuggerOptionName], value);
    });

    this.view.history.replaceState(null, '', url.toString());
  }
}
